import React, { useRef, useState } from 'react';
import CreateModalTemplate from '@component/gridgio/modal/dialog-modal/create-modal-template';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import styled from 'styled-components';
import IconTextBlock from '@component/common/block/icon-text-block';
import TextBlock from '@component/common/block/text-block';
import TextInput from '@component/common/input/text-input';
import Gap from '@component/common/gap';
import { useUploadSourceMutation } from '@hook/gridgio/useApi';
import * as color from '@constant/color';
import * as size from '@constant/size';

const FieldContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: ${size.MARGIN_1};
`;

const FileContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: 52px;
  padding: ${size.MARGIN_1};
  align-items: center;
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_2};
  border-radius: ${size.BORDER_RADIUS_1};
  cursor: pointer;
`;

const TITLE_DESIGN = { text: { size: 'large', color: color.BLACK_2, bold: true } } as const;
const LABEL_DESIGN = {
  box: { width: '100%', alignX: 'left' },
  text: { size: 'medium', color: color.BLACK_2, bold: true },
} as const;
const INPUT_DESIGN = {
  box: {
    width: '100%',
    height: '42px',
    padding: `0 ${size.MARGIN_1}`,
    border: true,
    borderWidth: size.BORDER_WIDTH_1,
    borderColor: color.GRAY_2,
    borderRadius: size.BORDER_RADIUS_1,
    alignX: 'left',
  },
  text: { size: 'medium', color: color.BLACK_2 },
} as const;

const SourceCreateModal: React.FC = () => {
  const dispatch = useTypedDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<'input' | 'complete'>('input');
  const [name, setName] = useState('');
  const [drawingFile, setDrawingFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const sourceMutation = useUploadSourceMutation();
  const isPending = sourceMutation.isPending;

  const handleClose = () => {
    if (isPending) return;
    dispatch(modalActions.hideModal());
  };

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (phase !== 'input' || !trimmedName || !drawingFile || isPending) return;

    setErrorMessage('');
    try {
      await sourceMutation.mutateAsync({
        name: trimmedName,
        source: drawingFile,
      });
      setPhase('complete');
    } catch {
      setErrorMessage('도면을 등록하지 못했습니다. 파일을 확인한 뒤 다시 시도해주세요.');
    }
  };

  return (
    <CreateModalTemplate
      title='도면 등록'
      phase={phase}
      confirmText='등록하기'
      errorMessage={errorMessage}
      isPending={isPending}
      confirmDisabled={!name.trim() || !drawingFile}
      handleCancel={handleClose}
      handleConfirm={() => void handleCreate()}
      handleSubmit={() => void handleCreate()}
      handleComplete={handleClose}
      input={
        <>
          <FieldContainer>
            <TextBlock text='도면 이름' design={LABEL_DESIGN} />
            <TextInput
              name='source-name'
              value={name}
              placeholder='도면 이름을 입력해주세요'
              design={INPUT_DESIGN}
              onChange={(event) => setName(event.target.value)}
            />
          </FieldContainer>
          <Gap height={size.MARGIN_2} />
          <FieldContainer>
            <TextBlock text='도면 파일' design={LABEL_DESIGN} />
            <input
              ref={fileInputRef}
              type='file'
              accept='application/pdf,image/jpeg,image/png,image/webp'
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                setDrawingFile(file);
                if (file && !name.trim()) setName(file.name.replace(/\.[^.]+$/, ''));
              }}
            />
            <FileContainer
              onClick={() => {
                if (!isPending) fileInputRef.current?.click();
              }}>
              <IconTextBlock
                icon='folder-open'
                text={drawingFile?.name ?? 'PDF 또는 이미지 파일을 선택해주세요'}
                design={{
                  box: { width: '100%', gap: size.MARGIN_1, alignX: 'left' },
                  icon: { size: 'small', color: color.CYAN_1 },
                  text: { size: 'medium', color: drawingFile ? color.BLACK_2 : color.GRAY_1, oneline: true },
                }}
              />
            </FileContainer>
          </FieldContainer>
        </>
      }
      complete={<TextBlock text='도면이 등록되었습니다.' design={TITLE_DESIGN} />}
    />
  );
};

export default SourceCreateModal;
